import { create } from 'zustand';

import {
    useTradingSessionStore,
    type ConnectedWalletRequirement,
} from '@/features/tradingSession/store';

/**
 * Why the migration sends a passkey or social session through the Connected
 * Wallet window: the payout is authorized by an EIP-712 proof from the owner
 * wallet, exactly like a V1 withdrawal.
 */
export const MIGRATION_WALLET_REQUIREMENT: ConnectedWalletRequirement = {
    operation: 'Migrating',
    reason: 'security',
};

interface MigrationStoreState {
    isOpen: boolean;
    /**
     * Set when the dialog was closed to open the Connected Wallet window it
     * needs. Once that window mints a wallet authority the dialog opens again
     * (see `useMigrationWalletRequirement`); cleared when the window resolves
     * either way, so a cancelled window leaves it closed.
     */
    reopenAfterWalletAuthority: boolean;
    open: () => void;
    close: () => void;
    /** Close the dialog, open the Connected Wallet window, come back after. */
    requestWalletAuthority: () => void;
    clearReopenAfterWalletAuthority: () => void;
}

/**
 * The V1 to V2 migration dialog (plan 2026-10-04-v1-to-v2-migration): opened
 * by the header's Migrate button, mounted once in `App.tsx`.
 */
export const useMigrationStore = create<MigrationStoreState>((set) => ({
    isOpen: false,
    reopenAfterWalletAuthority: false,
    open: () => set({ isOpen: true }),
    close: () => set({ isOpen: false }),
    requestWalletAuthority: () => {
        // two modal dialogs cannot share the screen: this one steps aside
        // while the wallet window runs, and the falling-edge hook brings it
        // back when that window grants a wallet authority.
        set({ isOpen: false, reopenAfterWalletAuthority: true });
        useTradingSessionStore
            .getState()
            .requestConnectedWalletWindow(MIGRATION_WALLET_REQUIREMENT);
    },
    clearReopenAfterWalletAuthority: () =>
        set({ reopenAfterWalletAuthority: false }),
}));
